import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { TableHeader } from '../components/Common/Table/types';

type Options = {
  statusKey?: string;
  itemName?: string;
  allLabel?: string;
};

const useStatusFilter = <T extends Record<string, any>>(
  rows: T[],
  options: Options = {},
) => {
  const statusKey = options.statusKey || 'status';
  const itemName = options.itemName || 'status';
  const allLabel = options.allLabel || 'All statuses';

  const [statusFilter, setStatusFilter] = useState<string>('');

  const statusOptions = useMemo(() => {
    const set = new Set<string>();
    rows.forEach(row => {
      const value = (row?.[statusKey] ?? '').toString().trim();
      if (value) set.add(value);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [rows, statusKey]);

  const filteredRows = useMemo(() => {
    if (!statusFilter) return rows;
    return rows.filter(r => (r?.[statusKey] ?? '').toString() === statusFilter);
  }, [rows, statusKey, statusFilter]);

  const statusFilterNode = (
    <StatusSelectWrap>
      <StatusSelect
        value={statusFilter}
        onChange={e => setStatusFilter(e.target.value)}
        aria-label="Filter by status"
      >
        <option value="">{allLabel}</option>
        {statusOptions.map(opt => (
          <option key={opt} value={opt}>{opt}</option>
        ))}
      </StatusSelect>
    </StatusSelectWrap>
  );

  const withStatusFilter = (headers: TableHeader[]): TableHeader[] =>
    headers.map(h =>
      h.itemName === itemName
        ? { ...h, hideFilter: false, customFilter: statusFilterNode }
        : h,
    );

  return {
    statusFilter,
    setStatusFilter,
    statusOptions,
    filteredRows,
    statusFilterNode,
    withStatusFilter,
  };
};

export default useStatusFilter;

const StatusSelectWrap = styled.div`
  position: relative;
  margin: 0 10px 10px 10px;
  background: var(--neutral-0);
  border: 1px solid var(--border);
  border-radius: 8px;
  height: 30px;
  min-width: 0;
  display: flex;
  align-items: center;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &:hover {
    border-color: var(--neutral-400);
  }

  &:focus-within {
    border-color: var(--primary-600);
    box-shadow: var(--shadow-focus);
  }

  &::after {
    content: '';
    position: absolute;
    right: 12px;
    top: 50%;
    width: 0;
    height: 0;
    border-left: 4px solid transparent;
    border-right: 4px solid transparent;
    border-top: 5px solid var(--neutral-500);
    transform: translateY(-50%);
    pointer-events: none;
  }
`;

const StatusSelect = styled.select`
  flex: 1;
  min-width: 0;
  height: 100%;
  padding: 0 28px 0 12px;
  border: none;
  outline: none;
  background: transparent;
  font-family: inherit;
  font-size: 0.8125rem;
  color: var(--mainText);
  cursor: pointer;
  appearance: none;
  -webkit-appearance: none;
`;
